/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Tells our linter that chrome is a global variable
/* global chrome */

import { Link } from 'react-router-dom';

// reactstrap components
import { Form, Col } from 'reactstrap';

import { useState, useContext, useEffect, useReducer, useRef } from 'react';
import { GlobalContext } from '../api/GlobalContext';
import { useNavigate } from 'react-router-dom';
import getDeviceData from '../api/DeviceInfo';
import {
  getSessionData,
  getPageUrl,
} from '../EMR_Integration/react_webInjector.ts';
import { storeLocalData } from '../api/Common';
import { isNativeRecorder, sendNativeMessage } from '../api/nativeRecorder';
import { logGAEvent } from '../api/googleAnalytics';
import Modal from '../components/Modals/Modal';
import GoogleSignInButton, {
  GOOGLE_SSO_STARTED,
} from '../components/Misc/GoogleSignInButton';
import MicrosoftSignInButton, {
  MICROSOFT_SSO_STARTED,
} from '../components/Misc/MicrosoftSignInButton';

// Import SSO provider images
import CoraHealthLogo from '../assets/img/emrs/CoraHealth.png';
import ConfluentHealthLogo from '../assets/img/emrs/ConfluentHealth.png';

const CORA_SSO_URL =
  'https://launcher.myapps.microsoft.com/api/signin/6d527a2b-32ef-4d98-8c7e-4be31fe643e5?tenantId=5d1837c8-8290-4cf9-83e1-c0b1ff56dc97';
const CONFLUENT_SSO_URL =
  'https://confluenthealth.okta.com/home/confluenthealth_comprehendhealth_1/0oa20n97vzgiJyNvb0h8/aln20n9co0sBN9xWq0h8';
const isCX = window.location.protocol === 'chrome-extension:';

// wrap getSessionData and getPageUrl in a try catch that times out after 1 second...The normal one just hangs.
async function getSessionDataWithTimeout() {
  try {
    return await Promise.race([
      getSessionData(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 1000),
      ),
    ]);
  } catch (err) {
    console.warn('getSessionDataWithTimeout failed:', err.message);
    return null; // ⬅️ prevent crash and infinite retry
  }
}

// We error if we try this before the page had loaded, in that case we retry 5 times. each with a 100ms delay.
async function getPageUrlWithTimeout() {
  let retryCount = 0;
  const maxRetries = 5;

  while (retryCount < maxRetries) {
    try {
      return await Promise.race([
        getPageUrl(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 1000),
        ),
      ]);
    } catch (error) {
      retryCount++;
      if (retryCount >= maxRetries) {
        console.warn(
          'getPageUrlWithTimeout failed after retries:',
          error.message,
        );
        return null; // ⬅️ prevent infinite retries
      }
      // Wait a bit before retrying
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
}

// Close the EMR SSO Comprehend Tab once we have logged in.
function closeSSOTab() {
  chrome.tabs.query({ active: true, currentWindow: true }, function (tabs) {
    if (tabs[0]) {
      chrome.tabs.remove(tabs[0].id);
    }
  });
}

// function to check if current page URL includes a given domain fragment.
async function isOnProviderPage(domainFragment) {
  try {
    const urlObject = await getPageUrlWithTimeout();
    return urlObject?.url?.includes(domainFragment) || false;
  } catch (error) {
    return false;
  }
}

// Start the SSO flow.
function startSSOFlow(url, sessionStorageKey) {
  sessionStorage.setItem(sessionStorageKey, 'true');
  if (window.location.href.includes('chrome-extension://')) {
    chrome.tabs.create({ url });
  } else {
    window.location.href = url;
  }
}

const Login = () => {
  // Get the API
  const { apiClient } = useContext(GlobalContext);

  // Get the navigation function
  const navigate = useNavigate();

  // Force re-render capability
  const [u, forceUpdate] = useReducer((x) => x + 1, 0);

  // // State variable to store the label text
  const [labelText, setLabelText] = useState('Sign in');

  // State variables to store the form input values
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [disableCreateAccount, setDisableCreateAccount] = useState(false);
  const [deviceData, setDeviceData] = useState({});
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [showSSOModal, setShowSSOModal] = useState(false);
  // COM-407: device currently holding this account's session
  const [conflictDevice, setConflictDevice] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isTypingPassword, setIsTypingPassword] = useState(false);
  const typingTimeoutRef = useRef(null);
  const hasCompletedSSOFlow = useRef(false);
  const embedAuthFlow = useRef(null);

  // Brute force protection state
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState(null);
  const [remainingLockoutTime, setRemainingLockoutTime] = useState(0);

  // COM-407: signed out because another device took over this account
  useEffect(() => {
    if (sessionStorage.getItem('signedOutElsewhere')) {
      sessionStorage.removeItem('signedOutElsewhere');
      setLabelText(
        'You were signed out because this account signed in on another device. Any recording in progress was stopped.',
      );
    }
  }, []);

  useEffect(() => {
    const receiveEmbedAuth = (event) => {
      const flow = embedAuthFlow.current;
      const result = event.data;
      if (
        !flow ||
        event.origin !== window.location.origin ||
        event.source !== flow.popup ||
        result?.type !== 'COMP_EMBED_AUTH_RESULT'
      ) return;

      const sessionKey = Number(result.sessionKey);
      if (!Number.isSafeInteger(sessionKey) || !result.sessionEmail) return;

      storeLocalData(String(sessionKey));
      storeLocalData(result.sessionEmail, true);
      apiClient.sessionKey = sessionKey;
      apiClient.sessionEmail = result.sessionEmail;
      flow.popup.postMessage(
        { type: 'COMP_EMBED_AUTH_ACK' },
        window.location.origin,
      );
      embedAuthFlow.current = null;
      navigate('/User/Home');
    };
    window.addEventListener('message', receiveEmbedAuth);
    return () => window.removeEventListener('message', receiveEmbedAuth);
  }, [apiClient, navigate]);

  // Native app EMR panel: after "Go to EMR" the native shell holds a one-time
  // code from the app (GoToEMR.js). Ask for it and trade it for a session.
  useEffect(() => {
    if (window.parent === window || !isNativeRecorder()) return;
    const receiveEmrHandoff = (event) => {
      if (
        event.source !== window.parent ||
        event.data?.__comp !== true ||
        event.data.type !== 'RN_MESSAGE'
      ) return;
      let message;
      try {
        message = JSON.parse(event.data.data);
      } catch (_) {
        return;
      }
      if (message?.type !== 'emrHandoff' || !message.value?.code) return;
      window.removeEventListener('message', receiveEmrHandoff);

      apiClient
        .JMPostPublic('/v1/auth/emrHandoff/redeem', { code: message.value.code })
        .then((data) => {
          const sessionKey = Number(data?.sessionKey);
          if (!Number.isSafeInteger(sessionKey) || !data.sessionEmail) return;

          storeLocalData(String(sessionKey));
          storeLocalData(data.sessionEmail, true);
          apiClient.sessionKey = sessionKey;
          apiClient.sessionEmail = data.sessionEmail;
          navigate('/User/Home');
        })
        .catch(() => {});
    };
    window.addEventListener('message', receiveEmrHandoff);
    sendNativeMessage('requestEmrHandoff');
    return () => window.removeEventListener('message', receiveEmrHandoff);
  }, [apiClient, navigate]);

  const handleOAuthStarted = (flow) => {
    if (flow) {
      if (!flow.popup) {
        setLabelText('Please allow pop-ups to finish signing in.');
        return;
      }
      embedAuthFlow.current = flow;
    }
    forceUpdate();
  };

  // Extracted SSO/session logic into a shared function
  const initiateSSOFlowAndSession = (provider) => {
    const fetchData = async () => {
      const data = await getDeviceData();
      setDeviceData(data);
    };
    const fetchSessionData = async () => {
      // Check if we're on a CORA or Confluent page and if so handle the SSO flow
      const isOnCORAPage = await isOnProviderPage('.corahealth.com');
      const isOnConfluentPage = await isOnProviderPage('.confluenthealth.com');

      if (isCX && (isOnCORAPage || isOnConfluentPage)) {
        isOnCORAPage
          ? startSSOFlow(CORA_SSO_URL, 'CORA_SSO_STARTED')
          : startSSOFlow(CONFLUENT_SSO_URL, 'CONFLUENT_SSO_STARTED');
        // await 100 ms to allow time for the sessionStorage to be updated.
        await new Promise((resolve) => setTimeout(resolve, 100));
        forceUpdate();
      } // We must force a re-render in order to start our retry logic on the CORA_SSO_STARTED stuff below. Without this we never start polling the page for the logic. Edge does this for us automatically....
      // We always want to start the sso flow, the startSSOFlow function will handle CX vs not CX
      else if (provider) {
        if (provider === 'cora') {
          startSSOFlow(CORA_SSO_URL, 'CORA_SSO_STARTED');
          // await 100 ms to allow time for the sessionStorage to be updated.
          await new Promise((resolve) => setTimeout(resolve, 100));
          forceUpdate(); // This is a no-op if we are not in CX.
        } else if (provider === 'confluent') {
          startSSOFlow(CONFLUENT_SSO_URL, 'CONFLUENT_SSO_STARTED');
          // await 100 ms to allow time for the sessionStorage to be updated.
          await new Promise((resolve) => setTimeout(resolve, 100));
          forceUpdate(); // This is a no-op if we are not in CX.
        }
      }
      let data = await getSessionDataWithTimeout();

      if (data) {
        const { sessionKey, sessionEmail } = data?.sessionData ?? {};
        if (sessionKey && sessionEmail) {
          hasCompletedSSOFlow.current = true;
          storeLocalData(sessionKey);
          storeLocalData(sessionEmail, true);
          apiClient.sessionKey = Number(sessionKey);
          // Don't forget to close the CORA tab if this was a CORA SSO login.

          if (
            sessionStorage.getItem('CORA_SSO_STARTED') ||
            sessionStorage.getItem('CONFLUENT_SSO_STARTED') ||
            sessionStorage.getItem(GOOGLE_SSO_STARTED) ||
            sessionStorage.getItem(MICROSOFT_SSO_STARTED)
          ) {
            closeSSOTab();
            sessionStorage.removeItem('CORA_SSO_STARTED');
            sessionStorage.removeItem('CONFLUENT_SSO_STARTED');
            sessionStorage.removeItem(GOOGLE_SSO_STARTED);
            sessionStorage.removeItem(MICROSOFT_SSO_STARTED);
          }

          navigate('/User/Home');
        }
      }
    };

    // Start fetching session data immediately in all cases.
    fetchSessionData();

    // Only set up retry logic if CORA or Confluent SSO was started. This logic is necessary as the SSO flow
    // make take some time to load, + there are multiple microsoft redirects that take place and time.
    // So give the user up to 60 seconds to login.
    if (
      sessionStorage.getItem('CORA_SSO_STARTED') ||
      sessionStorage.getItem('CONFLUENT_SSO_STARTED') ||
      sessionStorage.getItem(GOOGLE_SSO_STARTED) ||
      sessionStorage.getItem(MICROSOFT_SSO_STARTED)
    ) {
      const intervalId = setInterval(() => {
        if (!hasCompletedSSOFlow.current) {
          fetchSessionData();
        } else {
          clearInterval(intervalId);
        }
      }, 3000);

      // Stop retrying after 60 seconds
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
      }, 60000);

      // Cleanup function to stop retrying when component unmounts
      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
    fetchData();
  };

  // Brute force protection constants
  const MAX_LOGIN_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
  const PROGRESSIVE_DELAYS = [0, 2000, 5000, 10000, 20000]; // Progressive delays in ms

  // Load saved lockout state from localStorage on component mount
  useEffect(() => {
    const savedLockoutData = localStorage.getItem('loginLockout');
    if (savedLockoutData) {
      const { endTime, attempts } = JSON.parse(savedLockoutData);
      const now = new Date().getTime();

      if (endTime > now) {
        // Still locked out
        setIsLocked(true);
        setLockoutEndTime(endTime);
        setLoginAttempts(attempts);
        setRemainingLockoutTime(Math.ceil((endTime - now) / 1000));
      } else {
        // Lockout expired, clear localStorage
        localStorage.removeItem('loginLockout');
        setLoginAttempts(0);
      }
    }
  }, []);

  // Update remaining lockout time every second
  useEffect(() => {
    let interval;
    if (isLocked && lockoutEndTime) {
      interval = setInterval(() => {
        const now = new Date().getTime();
        const remaining = Math.ceil((lockoutEndTime - now) / 1000);

        if (remaining <= 0) {
          // Lockout expired
          setIsLocked(false);
          setLockoutEndTime(null);
          setLoginAttempts(0);
          setRemainingLockoutTime(0);
          localStorage.removeItem('loginLockout');
          setLabelText('Sign in');
        } else {
          setRemainingLockoutTime(remaining);
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLocked, lockoutEndTime]);

  // Helper function to format time remaining
  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Helper function to handle failed login attempt
  const handleFailedLogin = (errorMessage) => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);

    if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
      // Lock out the user
      const endTime = new Date().getTime() + LOCKOUT_DURATION;
      setIsLocked(true);
      setLockoutEndTime(endTime);
      setRemainingLockoutTime(Math.ceil(LOCKOUT_DURATION / 1000));

      // Save to localStorage to persist across page refreshes
      localStorage.setItem(
        'loginLockout',
        JSON.stringify({
          endTime,
          attempts: newAttempts,
        }),
      );

      setLabelText(
        <span style={{ color: '#dc3545' }}>
          <i className="fa-solid fa-lock" style={{ marginRight: '8px' }}></i>
          Account temporarily locked due to too many failed attempts. Try again
          in {formatTimeRemaining(Math.ceil(LOCKOUT_DURATION / 1000))} minutes.
        </span>,
      );
    } else {
      const attemptsRemaining = MAX_LOGIN_ATTEMPTS - newAttempts;
      setLabelText(
        <span style={{ color: '#dc3545' }}>
          {errorMessage} ({attemptsRemaining} attempt
          {attemptsRemaining !== 1 ? 's' : ''} remaining)
        </span>,
      );
    }
  };

  // Helper function to handle successful login
  const handleSuccessfulLogin = () => {
    // Clear any lockout data
    setLoginAttempts(0);
    setIsLocked(false);
    setLockoutEndTime(null);
    setRemainingLockoutTime(0);
    localStorage.removeItem('loginLockout');
  };

  // The Google / Microsoft callbacks redirect to /auth/login?googleError=...
  // (or ?microsoftError=...) when they can't complete the flow. Show it once,
  // then scrub it so a refresh doesn't repeat.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError =
      params.get('googleError') || params.get('microsoftError');
    if (!ssoError) return;

    setLabelText(
      <span style={{ color: '#dc3545' }}>
        <i
          className="fa-solid fa-triangle-exclamation"
          style={{ marginRight: '8px' }}
        ></i>
        {ssoError}
      </span>,
    );
    params.delete('googleError');
    params.delete('microsoftError');
    const search = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (search ? `?${search}` : '') + window.location.hash,
    );
  }, []);

  useEffect(() => {
    initiateSSOFlowAndSession();
  }, [u]); // Force re-render when u changes.

  // Function to handle form submission
  function handleSubmit(e) {
    e.preventDefault();

    // Check if account is locked
    if (isLocked) {
      setLabelText(
        <span style={{ color: '#dc3545' }}>
          <i className="fa-solid fa-lock" style={{ marginRight: '8px' }}></i>
          Account locked. Try again in{' '}
          {formatTimeRemaining(remainingLockoutTime)}.
        </span>,
      );
      return;
    }

    setDisableCreateAccount(true);

    // Implement progressive delay based on previous failed attempts
    const delay =
      loginAttempts < PROGRESSIVE_DELAYS.length
        ? PROGRESSIVE_DELAYS[loginAttempts]
        : PROGRESSIVE_DELAYS[PROGRESSIVE_DELAYS.length - 1];

    if (delay > 0) {
      setLabelText(
        `Please wait ${delay / 1000} seconds before trying again...`,
      );
      setTimeout(() => {
        performLogin();
      }, delay);
    } else {
      performLogin();
    }
  }

  // Separate function to perform the actual login
  function performLogin(takeover = false) {
    // Tell the user we got their request
    setLabelText('Attempting to sign in...');
    // Call the login function with the form values
    apiClient
      .login(
        email,
        password,
        remember,
        deviceData.IP,
        deviceData.osType,
        deviceData.osVersion,
        deviceData.browser,
        deviceData.userAgent,
        deviceData.appVersion,
        mfaCode && mfaCode.length > 0 ? mfaCode : '', // Include the MFA code if available
        takeover,
      )
      .then((data) => {
        // If there is an error, display it in the Sign In Area
        if (data?.emailVerificationRequired) {
          // The credentials were fine - the account just hasn't been verified
          // yet. That isn't a failed attempt, so it must not count toward the
          // brute-force lockout.
          setLabelText('Please verify your email address to sign in.');
          navigate('/auth/verify/sent?email=' + encodeURIComponent(email));
        } else if ('error' in data) {
          // Handle failed login attempt with brute force protection
          handleFailedLogin(data['error']);
        } else if (data?.sessionConflict) {
          setConflictDevice(data.device || 'another device');
          setLabelText('Sign in');
        } else if (data?.mfaRequired) {
          // If MFA is required, set the MFA required state to true
          setMfaRequired(true);
          setLabelText('Enter the security code from Duo mobile App');
        } else {
          console.log('Login response:', data);
          // Handle successful login
          handleSuccessfulLogin();

          // Otherwise redirect home
          navigate('/');
          // Log the sign-in event
          logGAEvent('login', {
            email: email,
            name: data.name,
            referralCode: data.referralCode,
          });
        }
        setDisableCreateAccount(false);
      })
      .catch((e) => {
        // Handle network/system errors with brute force protection
        handleFailedLogin('Network error. Please try again.');
        setDisableCreateAccount(false);
      });
  }

  // Add SSO button click handler
  const handleSSOButtonClick = (e) => {
    e.preventDefault();
    setShowSSOModal(true);
  };

  // Add handler for SSO option click
  const handleSSOClick = (provider) => {
    initiateSSOFlowAndSession(provider);
    setShowSSOModal(false);
  };

  // Helper to render reusable SSO card
  const renderSSOCard = (provider, label, description, logo) => (
    <div
      className="sso-provider-card text-center"
      onClick={() => handleSSOClick(provider)}
      style={{
        cursor: 'pointer',
        minWidth: '140px',
        padding: '20px 16px',
        border: '2px solid #e7e2d6',
        borderRadius: '16px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(20,35,28,0.08)',
        transition: 'all 0.3s ease',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#2dce89';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(45, 206, 137, 0.2)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e7e2d6';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(20,35,28,0.08)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div className="mb-3">
        <img
          src={logo}
          alt={label}
          style={{
            width: '80px',
            height: '60px',
            objectFit: 'contain',
            marginBottom: '8px',
          }}
        />
      </div>
      <h6 className="mb-1 font-weight-bold" style={{ color: '#14231c' }}>
        {label}
      </h6>
      <small className="text-muted d-block" style={{ fontSize: '12px' }}>
        {description}
      </small>
    </div>
  );

  return (
    <>
      <Col lg="5" md="7">
        <div className="cptx-auth">
          <div className="cptx-card">
            <div className="cptx-card-body">
              <h2 className="cptx-title">Welcome back</h2>
              {labelText === 'Sign in' ? (
                <p className="cptx-subtitle">
                  Sign in to pick up where you left off.
                </p>
              ) : (
                <div className="cptx-status">
                  <small>{labelText}</small>
                </div>
              )}

              <GoogleSignInButton
                label="Continue with Google"
                onStarted={handleOAuthStarted}
              />

              <div style={{ marginTop: '10px' }}>
                <MicrosoftSignInButton
                  label="Continue with Microsoft"
                  onStarted={handleOAuthStarted}
                />
              </div>

              <button
                className="cptx-btn-outline"
                style={{ marginTop: '10px' }}
                type="button"
                disabled={disableCreateAccount}
                onClick={handleSSOButtonClick}
              >
                Continue with SSO
              </button>

              <div className="cptx-divider">
                <span>or</span>
              </div>
              <Form role="form" onSubmit={handleSubmit}>
                <div className="cptx-field">
                  <div className="cptx-field-row">
                    <label className="cptx-label">Email</label>
                  </div>
                  <input
                    className="cptx-input"
                    placeholder="you@clinic.com"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      const newEmail = e.target.value;
                      setEmail(newEmail);
                      if (newEmail.endsWith('@corahealth.com')) {
                        if (
                          window.location.href.includes('chrome-extension://')
                        ) {
                          setLabelText(
                            <span style={{ color: '#dc3545' }}>
                              <i
                                className="fa-solid fa-triangle-exclamation"
                                style={{ marginRight: '8px' }}
                              ></i>
                              For a Cora Health employee, to access Comprehend,
                              go to the CORA Connect Home. Under CORA Links,
                              click Comprehend Health to automatically sign up.
                            </span>,
                          );
                          setDisableCreateAccount(false);
                        } else {
                          window.location.href = CORA_SSO_URL;
                        }
                      }
                    }}
                  />
                </div>

                <div className="cptx-field">
                  <div className="cptx-field-row">
                    <label className="cptx-label">Password</label>
                    <Link className="cptx-hint-link" to="/auth/reset/email">
                      Forgot?
                    </Link>
                  </div>
                  <div className="cptx-input-wrap">
                    <input
                      className="cptx-input cptx-input-has-eye"
                      placeholder="••••••••••"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setIsTypingPassword(true);
                        if (typingTimeoutRef.current)
                          clearTimeout(typingTimeoutRef.current);
                        typingTimeoutRef.current = setTimeout(() => {
                          setIsTypingPassword(false);
                        }, 800);
                      }}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setIsPasswordFocused(false), 100)
                      }
                    />
                    <button
                      type="button"
                      className="cptx-input-eye"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        opacity:
                          isPasswordFocused || isTypingPassword || showPassword
                            ? 1
                            : 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <i
                        className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                      />
                    </button>
                  </div>
                </div>

                {mfaRequired && (
                  <div className="cptx-field">
                    <label className="cptx-label">MFA code</label>
                    <input
                      className="cptx-input"
                      placeholder="000000"
                      type="text"
                      autoComplete="one-time-code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                    />
                  </div>
                )}

                <div
                  className={`cptx-check-row${remember ? ' cptx-checked' : ''}`}
                  onClick={() => setRemember(!remember)}
                >
                  <input
                    className="cptx-checkbox-native"
                    id="customCheckLogin"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <div className="cptx-checkbox-box" />
                  <p>Remember me</p>
                </div>

                <button
                  className="cptx-btn-primary"
                  type="submit"
                  disabled={disableCreateAccount || isLocked}
                >
                  {isLocked ? (
                    <>
                      <i
                        className="fa-solid fa-lock"
                        style={{ marginRight: '8px' }}
                      ></i>
                      Locked ({formatTimeRemaining(remainingLockoutTime)})
                    </>
                  ) : loginAttempts > 0 &&
                    loginAttempts < MAX_LOGIN_ATTEMPTS ? (
                    <>
                      <i
                        className="fa-solid fa-triangle-exclamation"
                        style={{ marginRight: '8px' }}
                      ></i>
                      Sign in ({MAX_LOGIN_ATTEMPTS - loginAttempts} attempts
                      left)
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </Form>

              <p className="cptx-footer-note">
                New to Comprehend PT?{' '}
                <Link to="/auth/register">Create your account</Link>
              </p>
            </div>
          </div>
        </div>
      </Col>

      {/* COM-407: account already signed in elsewhere */}
      <Modal
        isOpen={!!conflictDevice}
        onClose={() => setConflictDevice(null)}
        buttons={[
          {
            label: 'Cancel',
            color: 'grey',
            onClick: () => setConflictDevice(null),
          },
          {
            label: 'Sign in here',
            color: 'primary',
            onClick: () => {
              setConflictDevice(null);
              performLogin(true);
            },
          },
        ]}
      >
        <h3>This account is already signed in</h3>
        <p>
          This account is currently signed in on <b>{conflictDevice}</b>.
          Accounts are for one person and shouldn't be shared.
        </p>
        <p>Signing in here will sign out the other device.</p>
      </Modal>

      {/* SSO Provider Selection Modal */}
      <Modal
        isOpen={showSSOModal}
        onClose={() => setShowSSOModal(false)}
        title="Choose SSO Provider"
        size="lg"
        // buttons={ssoModalButtons}
        isCloseButton={false}
      >
        <div className="text-center">
          <p className="mb-4">
            Select your organization's SSO provider to continue:
          </p>
          <div
            className="d-flex justify-content-center"
            style={{ gap: '20px' }}
          >
            {renderSSOCard(
              'cora',
              'Cora Health',
              'Microsoft Azure SSO',
              CoraHealthLogo,
            )}
            {renderSSOCard(
              'confluent',
              'Confluent',
              'Confluent SSO',
              ConfluentHealthLogo,
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Login;
